import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { sendReplyOnMessage } from "../../apis";
import Modal from "react-modal";
import SendFileAnimationComponent from "../../assets/lottie/SendFileAnimation/SendFileAnimationComponent";
import useReplies from "./useReplies";

const SendReplyFromAdminToPartner = ({
  currentMessageId,
  setRepliesData,
  setPagination,
}) => {
  const { fetchReplies } = useReplies();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onSubmitHandler = async (values, { resetForm }) => {
    const formData = new FormData();
    formData.append("message", values.message);
    if (selectedImage) {
      formData.append("message_attache", selectedImage);
    }

    setIsSubmitting(true);
    sendReplyOnMessage(currentMessageId, formData)
      .then(() => {
        resetForm();
        setSelectedImage(null); // Clear the selected image
        fetchReplies({
          messageId: currentMessageId,
          scrollToBottom: true,
        }).then((result) => {
          if (result) {
            const { repliesData, pagination } = result;
            setRepliesData(repliesData);
            setPagination(pagination);
          }
        });
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Failed to send message",
          text: error.response?.data?.message || "Please try again later.",
          confirmButtonColor: "#d33",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
        closeModal(); // Close modal after submission
      });
  };

  const formik = useFormik({
    initialValues: {
      message: "",
    },
    validationSchema: Yup.object({
      message: Yup.string().trim().required("Message is required"),
    }),
    onSubmit: onSubmitHandler,
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setSelectedImage(null);
    setIsModalOpen(false);
  };

  return (
    <>
      <footer className="tw-bg-white tw-border-t tw-border-gray-300 tw-p-2 tw-w-full tw-absolute tw-left-0 tw-bottom-0 tw-z-40">
        <form
          onSubmit={formik.handleSubmit}
          className="tw-flex tw-items-center tw-gap-2"
        >
          <input
            type="text"
            name="message"
            placeholder="Type a message..."
            className={`tw-w-full tw-p-2 tw-rounded-md tw-border tw-focus:outline-none tw-focus:border-blue-500`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.message}
          />

          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="tw-hidden"
            id="imageUpload"
          />
          <label htmlFor="imageUpload" className="!tw-m-0 cursor-pointer">
            <SendFileAnimationComponent className={"tw-w-9"} />
          </label>

          <button
            disabled={formik.errors.message}
            type="submit"
            className={`${
              formik.errors.message
                ? "tw-bg-gray-400"
                : "tw-bg-primary hover:tw-bg-primaryLight"
            }  tw-text-white tw-px-4 tw-py-2 !tw-rounded-md tw-min-w-24 tw-ml-2`}
          >
            {isSubmitting ? "Sending..." : "Send"}
          </button>
        </form>
      </footer>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Image Preview"
        className="tw-bg-white tw-rounded-lg tw-p-4 tw-max-w-md tw-w-full tw-mx-auto tw-my-auto"
        overlayClassName="tw-fixed tw-inset-0 tw-bg-black/70"
      >
        <button
          onClick={closeModal}
          className="tw-absolute tw-text-white tw-bg-red-500 tw-rounded-full tw-items-center !tw-p-0 tw-w-7 tw-h-7 tw-flex tw-justify-center tw-top-2 tw-right-2 hover:tw-text-gray-300"
        >
          <span class="material-icons !tw-text-sm">close</span>
        </button>

        {selectedImage && (
          <img
            src={URL.createObjectURL(selectedImage)}
            alt="Selected Preview"
            className="tw-max-w-full tw-h-auto tw-m-auto tw-rounded-lg"
          />
        )}
        <form onSubmit={formik.handleSubmit} className="tw-mt-4 tw-text-end">
          <input
            type="text"
            name="message"
            placeholder="Type your message..."
            className={`tw-w-full tw-p-2 tw-rounded-md tw-border tw-focus:outline-none tw-focus:border-blue-500`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.message}
          />
          {formik.touched.message && formik.errors.message && (
            <div className="tw-text-red-500 tw-text-start">
              {formik.errors.message}
            </div>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`${
              formik.touched.message && formik.errors.message
                ? "tw-bg-gray-400"
                : "tw-bg-primary"
            } tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-mt-2 ${
              isSubmitting ? "tw-opacity-50 tw-cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Sending..." : "Send"}
          </button>
        </form>
      </Modal>
    </>
  );
};

export default SendReplyFromAdminToPartner;
